import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  AlertCircle,
  CheckCircle2,
  Eye,
  EyeOff,
  Lock,
  Mail,
  RotateCcw,
  ShieldCheck,
} from "lucide-react";

import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Label } from "@/components/ui/label";
import { getRoleHomePath as getPostLoginPath } from "@/lib/navigation";
import { TESTIDS } from "@/lib/testids";
import {
  consumeSessionExpiredNotice,
  consumeStoredRedirectFrom,
} from "@/lib/authSession";

const AdminLogin = () => {
  const {
    login,
    verifyLogin,
    register,
    verifyRegistration,
    isAuthenticated,
    loading,
    role,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [mode, setMode] = useState("login");
  const [step, setStep] = useState("credentials");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [otp, setOtp] = useState("");
  const [trustDevice, setTrustDevice] = useState(false);
  const [pendingFlow, setPendingFlow] = useState("login");
  const [sessionExpiredNotice, setSessionExpiredNotice] = useState(() => {
    return Boolean(location.state?.sessionExpired || consumeSessionExpiredNotice());
  });

  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const flowLabel = pendingFlow === "register" ? "verification du compte" : "connexion";

  const getTargetDestination = (resolvedRole) => {
    const fromPath =
      location.state?.from?.pathname
        ? `${location.state.from.pathname}${location.state.from.search || ""}`
        : consumeStoredRedirectFrom();
    if (fromPath && !fromPath.startsWith("/admin/login") && !fromPath.startsWith("/candidat/login")) {
      return fromPath;
    }
    return getPostLoginPath(resolvedRole);
  };

  if (!loading && isAuthenticated) {
    return <Navigate to={getTargetDestination(role)} replace />;
  }

  const resetFeedback = () => {
    setError(null);
    setSuccess(null);
  };

  const resetOtpStep = () => {
    setStep("credentials");
    setOtp("");
    setTrustDevice(false);
  };

  const switchMode = (nextMode) => {
    setMode(nextMode);
    setPendingFlow(nextMode);
    resetFeedback();
    resetOtpStep();
  };

  const handleChange = (event) => {
    resetFeedback();
    setFormData((currentData) => ({
      ...currentData,
      [event.target.name]: event.target.value,
    }));
  };

  const formatOtpMessage = (result) => {
    if (result?.rateLimited && result?.retryAfterSeconds) {
      return `Un code a deja ete envoye. Reessayez dans ${result.retryAfterSeconds}s.`;
    }

    return result?.message || "Un code a 6 chiffres vient d'etre envoye a votre adresse.";
  };

  const startOtpFlow = async (currentMode) => {
    const authAction = currentMode === "register" ? register : login;
    const result = await authAction(formData.email, formData.password);

    if (!result.success) {
      setError(result.error);
      return false;
    }

    if (result.authenticated) {
      navigate(getTargetDestination(result.role), { replace: true });
      return true;
    }

    setPendingFlow(currentMode);
    setStep("otp");
    setOtp("");
    setSuccess(formatOtpMessage(result));
    return true;
  };

  const handleCredentialsSubmit = async (event) => {
    event.preventDefault();
    resetFeedback();

    setIsSubmitting(true);

    try {
      await startOtpFlow(mode);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOtpSubmit = async (event) => {
    event.preventDefault();
    resetFeedback();

    if (otp.length !== 6) {
      setError("Saisissez le code a 6 chiffres.");
      return;
    }

    setIsSubmitting(true);

    try {
      const verifyAction = pendingFlow === "register" ? verifyRegistration : verifyLogin;
      const result = await verifyAction(formData.email, otp, trustDevice);

      if (result.success) {
        navigate(getTargetDestination(result.role), { replace: true });
        return;
      }

      setError(result.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendCode = async () => {
    resetFeedback();
    setIsSubmitting(true);

    try {
      await startOtpFlow(pendingFlow);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-lenvia-bg flex items-center justify-center">
        <div className="w-8 h-8 rounded-full border-2 border-lenvia-blue border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-lenvia-bg flex items-center justify-center p-4 sm:p-6">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-16 left-8 sm:left-20 w-64 sm:w-96 h-64 sm:h-96 bg-cyan-500/10 rounded-full blur-[100px]" />
        <div className="absolute bottom-12 right-8 sm:right-20 w-72 sm:w-[28rem] h-72 sm:h-[28rem] bg-sky-500/10 rounded-full blur-[110px]" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg relative z-10"
      >
        <div className="text-center mb-8">
          <Link
            to="/admin/login"
            aria-label="Lenvia"
            data-testid={TESTIDS.shell.logo}
            className="inline-flex justify-center"
          >
            <img src="/lenvia-logo.png" alt="Lenvia" className="h-12 mx-auto mb-4" />
          </Link>
          <h1 className="text-2xl font-heading font-bold text-white">
            Espace Admin
          </h1>
          <p className="text-slate-400 mt-2">
            {step === "otp"
              ? "Confirmez votre identite avec le code email"
              : mode === "register"
                ? "Activez votre acces collaborateur"
                : "Connectez-vous a votre espace securise"}
          </p>
        </div>

        <div className="glass rounded-3xl p-6 sm:p-8 border border-white/10">
          <div className="flex gap-2 mb-6">
            <button
              type="button"
              onClick={() => switchMode("login")}
              className={`flex-1 py-2.5 px-4 rounded-xl text-sm font-medium transition-colors ${mode === "login"
                ? "bg-lenvia-blue text-white"
                : "text-slate-400 hover:text-white hover:bg-white/5"
                }`}
            >
              Connexion
            </button>
            <button
              type="button"
              onClick={() => switchMode("register")}
              className={`flex-1 py-2.5 px-4 rounded-xl text-sm font-medium transition-colors ${mode === "register"
                ? "bg-lenvia-blue text-white"
                : "text-slate-400 hover:text-white hover:bg-white/5"
                }`}
            >
              Inscription
            </button>
          </div>

          {sessionExpiredNotice && !error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              role="alert"
              data-testid="admin-session-expired-alert"
              className="mb-4 p-3.5 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-start gap-3 text-amber-200 text-sm"
            >
              <AlertCircle className="w-5 h-5 mt-0.5 shrink-0 text-amber-400" />
              <div>
                <p className="font-semibold text-white">Session expirée</p>
                <p className="text-xs text-amber-200/90 mt-0.5">
                  Votre session a expiré pour des raisons de sécurité. Veuillez vous reconnecter pour continuer.
                </p>
              </div>
            </motion.div>
          )}

          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/20 flex items-start gap-2 text-red-300 text-sm"
            >
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </motion.div>
          )}

          {success && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-start gap-2 text-emerald-300 text-sm"
            >
              <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{success}</span>
            </motion.div>
          )}

          {step === "credentials" ? (
            <form onSubmit={handleCredentialsSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-300">
                  Email professionnel
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="prenom.nom@entreprise.com"
                    required
                    autoComplete="email"
                    data-testid="admin-auth-email"
                    className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-slate-500"
                  />
                </div>
                <p className="text-xs text-slate-500">
                  Les comptes externes doivent disposer d'une autorisation active.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-300">
                  Mot de passe
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="••••••••"
                    required
                    minLength={8}
                    autoComplete={mode === "register" ? "new-password" : "current-password"}
                    data-testid="admin-auth-password"
                    className="pl-10 pr-10 bg-white/5 border-white/10 text-white placeholder:text-slate-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((currentValue) => !currentValue)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
                <p className="text-xs text-slate-500">
                  Le mot de passe seul ne suffit jamais: un code OTP sera demande.
                </p>
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                data-testid="admin-auth-submit"
                className="w-full btn-primary"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                    {mode === "register" ? "Envoi du code..." : "Verification..."}
                  </span>
                ) : (
                  mode === "register" ? "Creer et verifier mon acces" : "Continuer"
                )}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleOtpSubmit} className="space-y-5">
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-lenvia-blue/15 flex items-center justify-center text-lenvia-blue">
                    <ShieldCheck className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">
                      Code de {flowLabel}
                    </p>
                    <p className="text-sm text-slate-400">
                      Nous avons envoye un code a 6 chiffres a <span className="text-slate-200">{formData.email}</span>.
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      Le code expire au bout de 10 minutes et ne s'utilise qu'une seule fois.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <Label htmlFor="otp" className="text-slate-300">
                  Code OTP
                </Label>
                <InputOTP
                  id="otp"
                  maxLength={6}
                  value={otp}
                  onChange={(value) => setOtp(value.replace(/\D/g, "").slice(0, 6))}
                  containerClassName="justify-center"
                  data-testid="admin-auth-otp"
                >
                  <InputOTPGroup>
                    {Array.from({ length: 6 }).map((_, index) => (
                      <InputOTPSlot
                        key={index}
                        index={index}
                        className="h-12 w-12 border-white/10 bg-white/5 text-base text-white"
                      />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>

              <div className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                <Checkbox
                  id="trust-device"
                  checked={trustDevice}
                  onCheckedChange={(checked) => setTrustDevice(checked === true)}
                  className="mt-1 border-white/20 data-[state=checked]:bg-lenvia-blue data-[state=checked]:border-lenvia-blue"
                />
                <div>
                  <Label htmlFor="trust-device" className="text-slate-200 cursor-pointer">
                    Faire confiance a cet appareil pendant 60 jours
                  </Label>
                  <p className="text-xs text-slate-500 mt-1">
                    Si active, vous n'aurez pas a saisir de code OTP sur cet appareil jusqu'a expiration ou revocation.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Button
                  type="submit"
                  disabled={isSubmitting || otp.length !== 6}
                  data-testid="admin-auth-otp-submit"
                  className="w-full btn-primary"
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                      Validation...
                    </span>
                  ) : (
                    "Valider le code"
                  )}
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  disabled={isSubmitting}
                  onClick={handleResendCode}
                  data-testid="admin-auth-resend"
                  className="w-full border-white/10 bg-white/5 text-slate-200 hover:bg-white/10 hover:text-white"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Renvoyer le code
                </Button>
              </div>

              <button
                type="button"
                onClick={() => {
                  resetFeedback();
                  resetOtpStep();
                }}
                className="w-full text-sm text-slate-500 hover:text-slate-300 transition-colors"
              >
                Modifier l'email ou le mot de passe
              </button>
            </form>
          )}

          <div className="mt-6 text-center">
            <a
              href="/"
              className="text-sm text-slate-500 hover:text-lenvia-blue transition-colors"
            >
              ← Retour au site
            </a>
          </div>
        </div>

        <p className="text-center text-xs text-slate-600 mt-4">
          Acces securise pour l'administration et les collaborateurs autorises
        </p>
      </motion.div>
    </div>
  );
};

export default AdminLogin;
